import React, { memo, useCallback, useEffect, useMemo, useState, MouseEvent } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { CirclePlusMinor } from '@shopify/polaris-icons'
import { useDispatch } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { BasicModal } from 'src/components/shared/modal/BasicModal'
import { TableSearch } from 'src/components/shared/table/TableSearch'
import { FetchOpportunities } from 'src/constants/actionTypes'
import { opportunityColumns } from 'src/constants/opportunity/opportunityGrid'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { Opportunity } from 'src/interfaces/opportunity'
import { colors } from 'src/theme'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { CreateOpportunityForm } from './create-opportunity-form/CreateOpportunityForm'
import { EmptyOpportunities } from './EmptyOpportunities'
import { OpportunitiesTableContext } from './OpportunitiesTable.context'
import { OpportunityActionMenu } from './OpportunityActionMenu'
import { ShareOpportunityModalContent } from './ShareOpportunityModalContent'

const getRowId = (row: Opportunity) => {
  return row.id
}

const OpportunitiesComponent = () => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const [opportunities, setOpportunities] = useState<Array<Opportunity>>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false)
  const [sharedOpportunity, setSharedOpportunity] = useState<Opportunity | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null)
  const [opportunityToAction, setOpportunityToAction] = useState<Opportunity | null>(null)

  const canCreate = hasPermission(UserPermission.OpportunitiesCreate)

  const fetchOpportunities = useCallback(async () => {
    setIsLoading(true)

    try {
      const result: { data: Opportunity[] } = await createAsyncAction(
        dispatch,
        FetchOpportunities.request(),
      )

      setOpportunities(result?.data || [])
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    fetchOpportunities()
  }, [fetchOpportunities])

  // Filtered here rather than on the server: the list endpoint takes no query, and an opportunity list is a handful
  // of rows per partner.
  const visibleOpportunities = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    if (!query) {
      return opportunities
    }

    return opportunities.filter((opportunity) => {
      const haystack = [
        opportunity.title,
        ...opportunity.markets.map((market) => {
          return market.title
        }),
      ]

      return haystack.some((value) => {
        return value?.toLowerCase().includes(query)
      })
    })
  }, [opportunities, searchQuery])

  const handleOpenAction = useCallback(
    (event: MouseEvent<HTMLButtonElement>, opportunity: Opportunity) => {
      setActionAnchor(event.currentTarget)
      setOpportunityToAction(opportunity)
    },
    [],
  )

  const handleCloseAction = useCallback(() => {
    setActionAnchor(null)
  }, [])

  const handleClearAction = useCallback(() => {
    setActionAnchor(null)
    setOpportunityToAction(null)
  }, [])

  const handleCreated = useCallback(
    (opportunity: Opportunity) => {
      setIsCreateOpen(false)
      setSharedOpportunity(opportunity)
      fetchOpportunities()
    },
    [fetchOpportunities],
  )

  const handleOpenCreate = useCallback(() => {
    setIsCreateOpen(true)
  }, [])

  const handleCloseCreate = useCallback(() => {
    setIsCreateOpen(false)
  }, [])

  const handleCloseShare = useCallback(() => {
    setSharedOpportunity(null)
  }, [])

  const renderEmpty = useCallback(() => {
    return <EmptyOpportunities isSearchUsed={Boolean(searchQuery)} />
  }, [searchQuery])

  const tableContext = useMemo(() => {
    return {
      refreshOpportunities: fetchOpportunities,
      actionAnchor,
      opportunityToAction,
      openOpportunityItemAction: handleOpenAction,
      closeOpportunityItemAction: handleCloseAction,
      clearOpportunityToAction: handleClearAction,
    }
  }, [
    fetchOpportunities,
    actionAnchor,
    opportunityToAction,
    handleOpenAction,
    handleCloseAction,
    handleClearAction,
  ])

  return (
    <OpportunitiesTableContext.Provider value={tableContext}>
      <div data-testid="opportunities-page">
        <div css={tw`flex flex-row items-start justify-between mb-6`}>
          <div>
            <Typography variant="h2" css={tw`mb-1`}>
              Opportunities
            </Typography>
            <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`max-w-[62ch]`}>
              Share an opportunity link wherever you advertise. Applications arrive in the candidate
              pool, and nothing is billed until you make an offer.
            </Typography>
          </div>
          {canCreate && (
            <Button
              className={tw`px-4`}
              color="dark"
              size="small"
              leftIcon={<CirclePlusMinor width={20} height={20} fill={colors.BASICS_WHITE} />}
              data-testid="opportunities-create-open"
              onClick={handleOpenCreate}
            >
              New opportunity
            </Button>
          )}
        </div>

        <div
          css={tw`flex flex-row items-center gap-4 px-4 py-3 bg-white rounded-t-lg border-0 border-b border-solid border-[#EDEDED]`}
        >
          <TableSearch
            searchQuery={searchQuery}
            onChangeSearchQuery={setSearchQuery}
            inputDataTestId="opportunities-search-input"
            clearButtonDataTestId="opportunities-search-clear-btn"
          />
        </div>

        <GridTable
          disableColumnFilter
          rows={visibleOpportunities}
          columns={opportunityColumns}
          getRowId={getRowId}
          isLoading={isLoading}
          EmptyElement={renderEmpty}
          hideFooter
          tableDataTestId="opportunities-list-table"
          rowDataTestIdPrefix="opportunities-list"
        />

        <OpportunityActionMenu />

        <BasicModal isOpen={isCreateOpen} onClose={handleCloseCreate}>
          <CreateOpportunityForm onClose={handleCloseCreate} onCreated={handleCreated} />
        </BasicModal>

        <BasicModal isOpen={Boolean(sharedOpportunity)} onClose={handleCloseShare}>
          {sharedOpportunity ? (
            <ShareOpportunityModalContent
              opportunity={sharedOpportunity}
              onClose={handleCloseShare}
            />
          ) : (
            <div />
          )}
        </BasicModal>
      </div>
    </OpportunitiesTableContext.Provider>
  )
}

export const Opportunities = memo(OpportunitiesComponent)
